import React, { memo, useMemo, useEffect, useCallback, CSSProperties, useState } from 'react';
import { PopperAnchor, Popper } from '@core/components/Popper';
import { formGroup, formControl, FormDataType, useFormConfig, useFormState } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import {
  FormInput,
  FormContainer,
  FormDateTimeInput,
  FormSelect,
  FormEmailSelect,
  FormCheckbox,
  FormColorPicker,
  FormPlaceAutoComplete,
} from '@core/components/form';
import { Button, IconButton } from '@core/components/Button';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { RecursivePartial } from '@core/utils/react';
import { CommonService, HTMLService, OrgService } from '@business/services';
import { tap } from 'rxjs/operators';
import CloseIcon from '@mui/icons-material/Close';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { Row, Col, Collapse } from 'reactstrap';
import moment from 'moment-timezone';
import { classnames } from '@core/utils/css';
import { useOptionalModalInstance } from '@core/components/ModalStack';
import { FlashMessage } from '@core/components/FlashMessage';
import { CompactUserResponse } from '@business/entities/account';
import {
  EventReminder,
  GCCalendarEventObjectType,
  GCCalendarEventResponse,
  GCCalendarEventStatus,
  GCCalendarEventStatuses,
} from '@business/entities/gc_calendar';
import { OrgAddressResponse, OrgId } from '@business/entities/organization';
import { openCannedResponseModal } from '@modules/common/SelectCannedResponse';
import { CannedResponseType } from '@business/entities/canned';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { EventReminderInput } from './EventReminderInput';
import { ModalContent, ModalDrawer, ModalHeader } from '@core/components/Modal';

function defineEventForm(name?: string, address?: OrgAddressResponse | null) {
  return formGroup({
    name: formControl<string>({
      label: 'Name',
      placeholder: 'Name',
      required: true,
      initialValue: name || '',
    }),
    start: formControl<number>({
      label: 'Start',
      placeholder: 'Start',
      required: true,
    }),
    duration: formControl<number>({
      label: 'Duration',
      placeholder: 'Duration',
      initialValue: 30,
    }),
    end: formControl<number>({
      label: 'End',
      placeholder: 'End',
    }),
    addToGoogle: formControl<boolean>({
      label: 'Add To Google',
    }),
    guestUsers: formControl<string[]>({
      placeholder: 'Select Users',
      label: 'Assign Users',
      required: true,
    }),
    guestEmails: formControl<string[]>({
      placeholder: 'Enter Email(s)',
      label: 'Client Emails',
      required: false,
      initialValue: [],
    }),
    description: formControl<string>({
      required: false,
      placeholder: 'Add Description',
    }),
    location: formControl<string>({
      label: 'Location',
      required: false,
      placeholder: 'Add Location',
      initialValue: address ? OrgService.getAddressString(address) : '',
    }),
    status: formControl<GCCalendarEventStatus>({
      label: 'Status',
      required: false,
    }),
    color: formControl<string>({
      label: 'Color',
      required: false,
      initialValue: '',
    }),
    reminders: formControl<EventReminder[]>({
      initialValue: [],
    }),
  });
}

export type CalendarEventFormData = FormDataType<typeof defineEventForm>;

export interface AddCalendarEventProps {
  anchor?: PopperAnchor<HTMLElement>;
  date: Moment | null;
  onSave: (data: RecursivePartial<GCCalendarEventResponse>, eventId?: string) => Observable<any>;
  style?: CSSProperties;
  selectedUserEmails?: string[];
  event?: GCCalendarEventResponse | null;
  users: CompactUserResponse[];
  objectType?: GCCalendarEventObjectType;
  objectId?: string;
  orgId?: OrgId;
  selectedGuestEmails?: string[];
  objectName?: string;
  objectAddress?: OrgAddressResponse | null;
}

function toCalendarEvent(data: CalendarEventFormData): RecursivePartial<GCCalendarEventResponse> {
  const participants: Array<{ userId?: string; email?: string; userType: string }> = [];

  (data.guestUsers || []).forEach(userId => {
    participants.push({ userId, userType: 'user' });
  });

  (data.guestEmails || []).forEach(email => {
    participants.push({ email, userType: 'client' });
  });

  return {
    name: data.name,
    location: data.location || '',
    description: data.description || '',
    participants,
    startTime: data.start,
    endTime: toMoment(data.start).add(data.duration, 'minutes').unix(),
    isSynced: data.addToGoogle || false,
    status: data.status,
    color: data.color || undefined,
    reminders: data.reminders || [],
  };
}

export const AddCalendarEvent = memo((props: AddCalendarEventProps) => {
  const { anchor, date, onSave, event, users, objectType, objectId, orgId, selectedUserEmails, selectedGuestEmails, objectName, objectAddress } =
    props;

  const form = useMemo(() => defineEventForm(objectName, objectAddress), [objectName, objectAddress]);
  const formValue = useNonNilObservable(form.value$);
  const [isNotes, setNotes] = useState(false);
  const modal = useOptionalModalInstance();
  const guestUsers = useMemo(() => (event ? event.participants.filter(p => p.userId).map(p => R.toString(p.userId!)) : []), [event]);
  const guestEmails = useMemo(() => (event ? event.participants.filter(p => p.email && !p.userId).map(p => p.email!) : []), [event]);
  const settings = useSettings();
  const isAutoCompleteEnabled = useMemo(() => settings.isGoogleMapsEnabled, [settings.isGoogleMapsEnabled]);

  useEffect(() => {
    if (event) {
      form.patchValue({
        name: event.name,
        description: event.description,
        location: event.location,
        guestUsers: guestUsers,
        guestEmails: guestEmails,
        start: event.startTime,
        end: event.endTime,
        duration: moment.unix(event.endTime).diff(moment.unix(event.startTime), 'minutes'),
        addToGoogle: event.isSynced || false,
        status: event.status,
        color: event.color || '',
        reminders: event.reminders || [],
      });
      if (!R.isEmpty(event.description)) {
        setNotes(true);
      }
    }
  }, [event, form, guestUsers, guestEmails]);

  useEffect(() => {
    if (!event && anchor?.visible) {
      form.patchValue({
        name: objectName || '',
        location: objectAddress ? OrgService.getCalendarAddressString(objectAddress) : '',
      });

      if (selectedUserEmails && selectedUserEmails.length > 0) {
        const selectedUserIds = users.filter(u => selectedUserEmails.includes(u.email)).map(u => R.toString(u.id));
        form.patchValue({ guestUsers: selectedUserIds });
      }
    }
  }, [anchor?.visible, event, form, selectedUserEmails, users, objectName, objectAddress]);

  // useEffect(() => {
  //   if (!event && anchor?.visible && selectedGuestEmails && selectedGuestEmails.length > 0) {
  //     form.patchValue({ guestEmails: selectedGuestEmails });
  //   }
  // }, [anchor?.visible, event, selectedGuestEmails, form]);

  const durationItems = useMemo(() => {
    const startTime = formValue.start;
    return [0, 15, 30, 45, 60, ...R.range(90, 1440, 30)].map(i => {
      const postFix = i > 45 ? `${i / 60} hour${i > 60 ? 's' : ''} ` : `${i} minutes`;
      const label = toMoment(startTime).add(i, 'minutes').format('h:mm a') + ` (${postFix})`;
      return {
        label: label,
        value: i,
      };
    });
  }, [formValue]);

  const closeButton = (
    <IconButton
      className="close_button"
      size="small"
      onClick={() => {
        anchor && anchor.onClose();
      }}
    >
      <CloseIcon />
    </IconButton>
  );

  const onSubmit = useCallback(
    (data: CalendarEventFormData) => {
      return onSave({ ...toCalendarEvent(data), objectType, objectId, orgId }, event ? event.localEventId || event.id : undefined).pipe(
        tap(() => form.reset(undefined, { emit: false })),
        tap(() => modal && modal.close()),
        tap(() => data.guestUsers.length > 0 && FlashMessage.success('Event saved successfully')),
      );
    },
    [event, form, modal, onSave, objectType, objectId, orgId],
  );

  const userItems = useMemo(
    () =>
      CommonService.selectItems(
        users,
        i => CommonService.getFullName(i),
        i => R.toString(i.id),
      ),
    [users],
  );

  useEffect(() => {
    if (date && !event) {
      form.patchValue({ start: date.unix(), end: date.unix(), duration: 30 });
    } else if (date && event) {
      form.patchValue({ start: date.unix(), end: date.unix() });
    }
  }, [date, form, event]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  const content = (
    <div className={classnames({ 'poper-modal': !!anchor })} style={{ width: anchor ? 600 : undefined, ...props.style }}>
      {!!anchor && <div className="border-bottom d-flex justify-content-end pb-2 mb-2">{closeButton}</div>}
      <FormContainer style={anchor ? { overflowY: 'auto', overflowX: 'hidden', maxHeight: 600 } : undefined}>
        <FormInput autoFocus control={form.controls.name} />
        {isAutoCompleteEnabled ? <FormPlaceAutoComplete control={form.controls.location} /> : <FormInput control={form.controls.location} />}
        <Row>
          <Col>
            <FormDateTimeInput control={form.controls.start} />
          </Col>
          <Col>
            <FormSelect control={form.controls.duration} items={durationItems} />
          </Col>
        </Row>
        <FormSelect type="multiple" isClearable control={form.controls.guestUsers} items={userItems} />
        <FormEmailSelect
          type="multiple"
          control={form.controls.guestEmails}
          selectedEmails={CommonService.selectItems(
            formValue.guestEmails || [],
            e => e,
            e => e,
          )}
          emails={CommonService.selectItems(
            selectedGuestEmails || [],
            e => e,
            e => e,
          )}
        />
        <FormCheckbox control={form.controls.addToGoogle} disabled={event && event.isSynced} />
        {/* <FormCheckbox control={form.controls.addToGoogle} disabled={!!event} /> */}

        {/* <FormEmailSelect
          type="multiple"
          dontPrefill
          control={form.controls.guestUsers}
          selectedEmails={CommonService.selectItems(
            formValue.guests || [],
            e => e,
            e => e,
          )}
          emails={props.availableEmails || []}
        /> */}
        {event && (
          <FormSelect
            control={form.controls.status}
            items={GCCalendarEventStatuses.map(status => ({ label: R.capitalize(status), value: status }))}
          />
        )}
        {!isNotes && (
          <div className="mb-2" style={{ cursor: 'pointer' }} onClick={() => setNotes(true)}>
            Add Description
          </div>
        )}
        <Collapse isOpen={isNotes}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <span
              style={{ cursor: 'pointer', textDecorationLine: 'underline' }}
              onClick={() =>
                openCannedResponseModal({
                  initialText: formValue.description,
                  type: CannedResponseType.calendarEvent,
                  title: 'Canned Templates',
                  pluralTitle: 'Templates',
                  singularTitle: 'Template',
                  simpleText: true,
                }).then(res => {
                  res
                    .pipe(
                      tap(re => {
                        form.patchValue({ description: formValue.description ? formValue.description + re.content : re.content });
                      }),
                    )
                    .subscribe();
                })
              }
            >
              Templates
            </span>
          </div>
          <FormInput type="textarea" control={form.controls.description} />
        </Collapse>
        <EventReminderInput value={formValue.reminders || []} onChange={reminders => form.patchValue({ reminders })} startTime={formValue.start} />
        <FormColorPicker control={form.controls.color} key={formValue.color || 'empty'} />
        <div>
          <Button type="button" onClick={() => form.submit()} className="w-100" loading={submitting}>
            Save
          </Button>
        </div>
      </FormContainer>
    </div>
  );

  if (!anchor) return content;
  return (
    <Popper anchor={anchor} placement="left" autoClose="none" aboveModal>
      {content}
    </Popper>
  );
});

export type AddCalendarEventDrawerProps = AddCalendarEventProps;

export const AddCalendarEventDrawer = memo((props: AddCalendarEventDrawerProps) => {
  return (
    <ModalDrawer maxWidth="md">
      <ModalHeader title="Add Event" />
      <ModalContent>
        <AddCalendarEvent {...props} />
      </ModalContent>
    </ModalDrawer>
  );
});
